"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const joi_1 = __importDefault(require("joi"));
const errors_1 = require("../../errors");
const IChargeTier_1 = require("../models/rates/IChargeTier");
class ChargeTierValidator {
    tierTypes = Object.values(IChargeTier_1.TierType);
    tierOperators = Object.values(IChargeTier_1.TierOperators);
    resetByList = Object.values(IChargeTier_1.TierResetBy);
    chargeTierSchema = joi_1.default.object({
        operator: joi_1.default.string()
            .valid(...this.tierOperators)
            .required(),
        from: joi_1.default.when('operator', [
            {
                is: IChargeTier_1.TierOperators.Noop,
                then: joi_1.default.number().min(0).max(0).required(),
                otherwise: joi_1.default.number().min(0).precision(4).required(),
            },
        ]),
        to: joi_1.default.when('operator', [
            {
                is: IChargeTier_1.TierOperators.Between,
                then: joi_1.default.number()
                    .min(0)
                    .precision(4)
                    .required()
                    .greater(joi_1.default.ref('from')),
                otherwise: joi_1.default.forbidden(),
            },
        ]),
        chargeAmount: joi_1.default.number().min(0).precision(4).required(),
    });
    chargeTiersSchema = joi_1.default.object({
        type: joi_1.default.string()
            .valid(...this.tierTypes)
            .required(),
        resetBy: joi_1.default.string()
            .valid(...this.resetByList)
            .optional(),
        tiers: joi_1.default.array().min(1).required(),
        chargeMinimumAmount: joi_1.default.number().min(0).precision(2).optional(),
    });
    validate(chargeTiers, jsonPath = '$') {
        const validationIssues = [];
        let canRunRangeValidation = true;
        const chargeTiersValidatonResult = this.chargeTiersSchema.validate(chargeTiers, { abortEarly: false, convert: false });
        if (chargeTiersValidatonResult.error) {
            const [rangeValidationFlag, newIssues] = this.validatonErrorHandler(chargeTiersValidatonResult.error, jsonPath, [], canRunRangeValidation);
            canRunRangeValidation = rangeValidationFlag;
            validationIssues.push(...newIssues);
        }
        if (!chargeTiers.tiers ||
            !Array.isArray(chargeTiers.tiers) ||
            chargeTiers.tiers.length < 1) {
            return validationIssues;
        }
        chargeTiers.tiers.forEach((chargeTier, index) => {
            const chargeTierValidationResult = this.chargeTierSchema.validate(chargeTier, { abortEarly: false, convert: false });
            if (chargeTierValidationResult.error) {
                const [rangeValidationFlag, newIssues] = this.validatonErrorHandler(chargeTierValidationResult.error, jsonPath, [], canRunRangeValidation, index);
                canRunRangeValidation = rangeValidationFlag;
                validationIssues.push(...newIssues);
            }
        });
        if (canRunRangeValidation) {
            if (chargeTiers.chargeMinimumAmount) {
                if (chargeTiers.tiers[0].operator != IChargeTier_1.TierOperators.Noop) {
                    validationIssues.push(new errors_1.Errors.ValidationIssue('IChargeTier', errors_1.Errors.ValidationErrorCode.notAllowed, 'chargeMinimumAmount', undefined, jsonPath));
                }
            }
            const validationIssue = this.rangeValidation(chargeTiers.tiers, jsonPath);
            if (validationIssue) {
                validationIssues.push(validationIssue);
            }
        }
        if (validationIssues.length > 0) {
            return validationIssues;
        }
        return;
    }
    validatonErrorHandler(error, jsonPath, validationIssues, canRunRangeValidation, index) {
        error.details.forEach((issue) => {
            // if the fieldName with an issue is one of "operator", "from" or "to" then the range validation cannot be performed
            canRunRangeValidation = ['operator', 'from', 'to'].includes(issue.context?.key)
                ? false
                : canRunRangeValidation;
            const errorPath = index !== undefined ? `${jsonPath}.tiers[${index}]` : jsonPath;
            switch (issue.type) {
                case 'any.required': {
                    validationIssues.push(new errors_1.Errors.ValidationIssue('IChargeTier', errors_1.Errors.ValidationErrorCode.requiredValue, issue.context?.key, undefined, errorPath));
                    break;
                }
                case 'array.min': {
                    canRunRangeValidation = false;
                    validationIssues.push(new errors_1.Errors.ValidationIssue('IChargeTier', errors_1.Errors.ValidationErrorCode.requiredValue, issue.context?.key, undefined, errorPath));
                    break;
                }
                case 'any.unknown': {
                    validationIssues.push(new errors_1.Errors.ValidationIssue('IChargeTier', errors_1.Errors.ValidationErrorCode.notAllowed, issue.context?.key, undefined, errorPath));
                    break;
                }
                case 'any.only':
                case 'number.greater':
                case 'number.min':
                case 'number.precision':
                case 'number.max': {
                    validationIssues.push(new errors_1.Errors.ValidationIssue('IChargeTier', errors_1.Errors.ValidationErrorCode.invalidValue, issue.context?.key, issue.context?.value, errorPath));
                    break;
                }
                case 'string.base':
                case 'object.base':
                case 'array.base': {
                    canRunRangeValidation = false;
                    validationIssues.push(new errors_1.Errors.ValidationIssue('IChargeTier', errors_1.Errors.ValidationErrorCode.invalidType, issue.context?.key, issue.context?.value, errorPath));
                    break;
                }
                case 'number.base': {
                    validationIssues.push(new errors_1.Errors.ValidationIssue('IChargeTier', errors_1.Errors.ValidationErrorCode.invalidType, issue.context?.key, issue.context?.value, errorPath));
                    break;
                }
            }
        });
        return [canRunRangeValidation, validationIssues];
    }
    rangeValidation(chargeTiers, jsonPath) {
        if (chargeTiers[0].operator === IChargeTier_1.TierOperators.Noop) {
            if (chargeTiers.length > 1) {
                return new errors_1.ValidationIssue('IChargeTier', errors_1.Errors.ValidationErrorCode.notAllowed, undefined, undefined, jsonPath);
            }
            return;
        }
        return this.rangeValidationAuxiliary(chargeTiers, jsonPath, 0);
    }
    rangeValidationAuxiliary(chargeTiers, jsonPath, currentIndex, prevIndex = 0) {
        if (currentIndex == 0) {
            if (chargeTiers[currentIndex].operator ==
                IChargeTier_1.TierOperators.LessThanOrEqual) {
                return this.rangeValidationAuxiliary(chargeTiers, jsonPath, currentIndex + 1, currentIndex);
            }
        }
        if (chargeTiers[currentIndex] === undefined) {
            if (chargeTiers[prevIndex].operator != IChargeTier_1.TierOperators.GreaterThan) {
                return new errors_1.ValidationIssue('IChargeTier', errors_1.Errors.ValidationErrorCode.notAllowed, 'operator', undefined, `${jsonPath}[${prevIndex}]`);
            }
            return;
        }
        if ((chargeTiers[prevIndex].operator == IChargeTier_1.TierOperators.Between ||
            chargeTiers[prevIndex].operator ==
                IChargeTier_1.TierOperators.LessThanOrEqual) &&
            (chargeTiers[currentIndex].operator == IChargeTier_1.TierOperators.Between ||
                chargeTiers[currentIndex].operator == IChargeTier_1.TierOperators.GreaterThan)) {
            const prevEndingValue = chargeTiers[prevIndex].operator == IChargeTier_1.TierOperators.Between
                ? chargeTiers[prevIndex].to
                : chargeTiers[prevIndex].from;
            if (prevEndingValue != chargeTiers[currentIndex].from) {
                return new errors_1.ValidationIssue('IChargeTier', errors_1.Errors.ValidationErrorCode.invalidValue, 'from', undefined, `${jsonPath}[${currentIndex}]`);
            }
            return this.rangeValidationAuxiliary(chargeTiers, jsonPath, currentIndex + 1, currentIndex);
        }
        return new errors_1.ValidationIssue('IChargeTier', errors_1.Errors.ValidationErrorCode.notAllowed, 'operator', undefined, `${jsonPath}[${currentIndex}]`);
    }
}
exports.default = ChargeTierValidator;
